// @flow
import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import { defineMessages, FormattedMessage, intlShape } from 'react-intl';
import { Helmet } from 'react-helmet';
import update from 'immutability-helper';
import classNames from 'classnames/bind';
import Loading from '../../../../elements/Loading/Loading';
import InfiniteScroll from '../../../../elements/InfiniteScroll/InfiniteScroll';
import Filter from '../../../../elements/Filter/Filter';
import FilterDrawer from '../../../../elements/Filter/Drawer/FilterDrawer';
import PaymentBox from './PaymentBox/PaymentBox';
import { generateTags } from '../../../../elements/Filter/utils';
import { oldOrderVerifier, orderVerifier } from '../../../../utils/string';
import AlertContainer from '../../../alert/container/AlertContainer';

import commonStyle from '../PaymentsViewCommon.scss';
import style from './PaymentsMade.scss';
import filterOptionsStyle from '../../../../assets/styles/filter-options.scss';
import filterStyles from '../../../../assets/styles/filter.scss';
import icons from '../../../../assets/styles/icons.scss';

import type { HTTPError } from '../../../../error';
import type { FilterCheckbox } from '../../../../elements/Filter/Filter';
import type { Invoice } from '../../../../entities';

type Props = {
  +fetchPaymentsMade: (queryFilter?: { [key: string]: string }, resetPagination?: boolean) => void;
  +isFetchingPaymentsMade?: boolean;
  +paymentsMade?: Invoice[];
  +canAsk?: boolean;
  +fetchPaymentsMadeError?: HTTPError;
  +resetPaymentsState: () => void;
  +resetAlertState: () => void;
};

type State = {
  drawerOpen: boolean;
  filter: {
    name: {
      value: string;
    };
    openName: boolean;
    paymentStates: {
      [key: string]: boolean;
    };
    openPaymentStates: boolean;
    years: {
      [key: string]: boolean;
    };
    openYears: boolean;
  };
  sort: {
    value: string;
    name: string;
  };
  order: {
    value: 'ASC' | 'DESC';
  };
};

const messages = defineMessages({
  title: {
    id: 'payments.made.title',
  },
  sortPlaceholder: {
    id: 'sort.title',
  },
  sortMostRecent: {
    id: 'sort.option.mostRecent',
  },
  sortLatest: {
    id: 'sort.option.latest',
  },
  sortHighestPrice: {
    id: 'sort.option.highestPrice',
  },
  sortLowestPrice: {
    id: 'sort.option.lowestPrice',
  }
});

const initialState = {
  drawerOpen: false,
  filter: {
    name: {
      value: '',
    },
    openName: true,
    paymentStates: {},
    openPaymentStates: false,
    years: {},
    openYears: false,
  },
  sort: {
    value: '',
    name: '',
  },
  order: {
    value: 'DESC',
  },
};

/*const payment = {
  id: 'TRPE03532157',
  state: 'RECEIVED',
  // pdf: 'https://bnetcmsus-a.akamaihd.net/cms/template_resource/8R5VLACJ34KX1533569042886.pdf',
  cancellations: [{
    name: 'iPhone 8 64gb',
    price: '1600',
    number: '1',
  }, {
    name: 'Nectar book by Libido',
    price: '15.20',
    number: '2',
  }],
  detail: {
    itemTotal: '2080',
    service: '20.22',
    commission: {
      total: '2025',
      netCommission: '250',
      tax: '150',
    }
  },
  items: [
    {
      name: 'iPhone 8 64gb',
      amount: 2,
      number: '1',
      price: '800'
    },
    {
      name: 'cosos de tienda cosas',
      amount: 10,
      number: '2',
      price: '5'
    },
    {
      name: 'One plus 64gb',
      amount: 1,
      number: '3',
      price: '500'
    },
    {
      name: 'Nectar book by Libido',
      amount: 1,
      number: '4',
      price: '15.20'
    },
    {
      name: 'Vampirines',
      amount: 120,
      number: '5',
      price: '0.58'
    },
  ],
  total: 5145.95,
};*/

export default class PaymentsMade extends Component<Props, State> {
  state = initialState;

  componentDidMount() {
    const { fetchPaymentsMade, paymentsMade } = this.props;
    if (!paymentsMade) {
      fetchPaymentsMade(this.buildQueryParams(), true);
    }
  }

  componentWillUnmount() {
    const { resetPaymentsState, resetAlertState } = this.props;
    resetPaymentsState();
    resetAlertState();
  }

  handleOpenDrawer = () => {
    this.setState({ drawerOpen: true });
  };

  handleCloseDrawer = () => {
    const { fetchPaymentsMade } = this.props;
    this.setState({
      drawerOpen: false,
    });
    fetchPaymentsMade(this.buildQueryParams(), true);
  };

  handleOpen = (name: string) => {
    this.setState((state: State) => {
      return {
        filter: update(state.filter, {
          [name]: { $set: !state.filter[name] }
        }),
      };
    });
  };

  handleChangeName(e: Event, isDesktop: boolean = true) {
    if (e.target instanceof HTMLInputElement) {
      const { value } = e.target;
      this.handleChangeFilterValueCreator(isDesktop)('name', { value });
    }
  }

  handleChangePaymentState(e: Event, isDesktop: boolean = true) {
    const { filter } = this.state;
    if (e.target instanceof HTMLInputElement) {
      const { value } = e.target;
      this.handleChangeFilterValueCreator(isDesktop)('paymentStates', { [value]: !filter.paymentStates[value] });
    }
  }

  handleChangeYear(e: Event, isDesktop: boolean = true) {
    const { filter } = this.state;
    if (e.target instanceof HTMLInputElement) {
      const { value } = e.target;
      this.handleChangeFilterValueCreator(isDesktop)('years', { [value]: !filter.years[value] });
    }
  }

  buildQueryParams(): { [key: string]: string } {
    const params = {};
    const { filter, sort, order } = this.state;
    const paymentStates = Object.keys(filter.paymentStates)
      .reduce((acum, act: string) => filter.paymentStates[act] ? acum.concat(act) : acum, []);
    const years = Object.keys(filter.years)
      .reduce((acum, act: string) => filter.years[act] ? acum.concat(act) : acum, []);
    if (filter.name.value) {
      if (oldOrderVerifier(filter.name.value) || orderVerifier(filter.name.value)) {
        params.order_id = filter.name.value;
      }
    }
    if (paymentStates.length) params.paymentStates = paymentStates.join(',');
    if (years.length) params.years = years.join(',');
    // Sort & Ord
    // if (sort.name) params.sort = sort.name;
    if (order.value) params.order = order.value;
    return params;
  }

  handleChangeSort = (e: Event) => {
    const { fetchPaymentsMade } = this.props;
    if (e.target instanceof HTMLSelectElement) {
      const option = e.target.options[e.target.selectedIndex];
      const dataset: { sort: string, order: 'ASC' | 'DESC' } = (option.dataset: any);
      this.setState({
        sort: {
          value: option.value,
          name: dataset.sort,
        },
        order: {
          value: dataset.order,
        },
      }, () => fetchPaymentsMade(this.buildQueryParams(), true));
    }
  };

  handleClearFilters = () => {
    const { fetchPaymentsMade } = this.props;
    this.setState(initialState,
      () => fetchPaymentsMade(this.buildQueryParams(), true));
  };

  handleDeleteTag = (key?: string, name?: string) => {
    const { fetchPaymentsMade } = this.props;
    const updateQuery = {};
    switch (key) {
      case 'paymentStates':
      case 'years':
        if (name) {
          updateQuery[key] = {
            [name]: { $set: false }
          };
        }
        break;
      case 'name':
        updateQuery[key] = {
          value: { $set: '' }
        };
        break;
    }
    this.setState((prevState: State) => update(prevState, {
      filter: updateQuery,
    }), () => fetchPaymentsMade(this.buildQueryParams(), true));
  };

  // Depends if desktop or mobile
  handleChangeFilterValueCreator = (isDesktop: boolean) =>
    (name: string, value: any) => {
      const { fetchPaymentsMade } = this.props;
      this.setState((state: State) => {
        const c = update(state.filter[name], { $merge: value });
        return {
          filter: update(state.filter, {
            [name]: { $set: c }
          }),
        };
      }, () => {
        if (isDesktop) {
          fetchPaymentsMade(this.buildQueryParams(), true);
        }
      });
    };

  generateFilterFields = (paymentStates: ?FilterCheckbox[], years: ?FilterCheckbox[], { filter }: State, isDesktop: boolean) => [
    {
      type: 'text',
      titleIntl: 'payments.filter.order.title',
      placeholderIntl: 'filter.name.placeholder',
      handler: (e: Event) => this.handleChangeName(e, isDesktop),
      stateValue: filter.name,
      name: 'name',
      icon: icons.symbolSearch,
      isOpen: filter.openName,
      handleOpen: () => this.handleOpen('openName'),
    },
    /*{
      type: 'checkbox',
      titleIntl: 'payments.filter.paymentStates.title',
      handler: (e: Event) => this.handleChangePaymentState(e, isDesktop),
      stateValue: filter.paymentStates,
      name: 'paymentStates',
      checkboxes: paymentStates,
      isOpen: filter.openPaymentStates,
      handleOpen: () => this.handleOpen('openPaymentStates'),
    },*/
    {
      type: 'checkbox',
      titleIntl: 'payments.filter.year.title',
      handler: (e: Event) => this.handleChangeYear(e, isDesktop),
      stateValue: filter.years,
      name: 'years',
      checkboxes: years,
      isOpen: filter.openYears,
      handleOpen: () => this.handleOpen('openYears'),
    },
  ];

  render() {
    const { drawerOpen, sort } = this.state;
    const { paymentsMade, isFetchingPaymentsMade, fetchPaymentsMade, canAsk } = this.props;
    const paymentStates = [
      { label: 'Payed', value: 'payed' },
      { label: 'Refunded', value: 'refunded' },
      { label: 'Received', value: 'received' },
      { label: 'Pending', value: 'pending' },
    ];
    const years = [
      {
        label: '2017',
        value: '2017',
      },
      {
        label: '2018',
        value: '2018',
      },
      {
        label: '2019',
        value: '2019',
      },
    ];
    const filterContent = <Filter
      filterFields={(this.generateFilterFields(paymentStates, years, this.state, true): any)}
      handleDeleteTag={this.handleDeleteTag}/>;
    const filterFields = this.generateFilterFields(paymentStates, years, this.state, true);
    const sortNativeSelect = (
      <select value={sort.value}
              onChange={this.handleChangeSort}>
        <option value="mostRecent" data-sort="DATE"
                data-order="DESC">{this.context.intl.formatMessage(messages.sortMostRecent)}</option>
        <option value="latest" data-sort="DATE"
                data-order="ASC">{this.context.intl.formatMessage(messages.sortLatest)}</option>
        {/*<option value="highestPrice" data-sort="PRICE"
                data-order="DESC">{this.context.intl.formatMessage(messages.sortHighestPrice)}</option>
        <option value="lowestPrice" data-sort="PRICE"
                data-order="ASC">{this.context.intl.formatMessage(messages.sortLowestPrice)}</option>*/}
      </select>
    );
    const tags: any = generateTags((filterFields: any), this.handleDeleteTag, this.context.intl.formatMessage);
    const noResult = (!paymentsMade || !paymentsMade.length) && !isFetchingPaymentsMade;
    const payments = paymentsMade && !!paymentsMade.length &&
      paymentsMade.map((invoice: Invoice, n: number) => <PaymentBox key={`box-${n}`}
                                                                    invoice={invoice}/>);

    return (
      <Fragment>
        <Helmet key="helmet">
          <title>{this.context.intl.formatMessage(messages.title)}</title>
        </Helmet>
        <section className={filterOptionsStyle.filterOptions}>
          <section className={filterOptionsStyle.header}>
            <FormattedMessage id="stores.filter.title">{
              text => <p className={filterOptionsStyle.title}><i className={icons.symbolTweak}/>{text}</p>
            }</FormattedMessage>
            <section className={filterOptionsStyle.sortOptions}>
              <FormattedMessage id="sort.title">{
                txt => <label>{txt}</label>
              }</FormattedMessage>
              {sortNativeSelect}
              {/*<i className={classNames(filterOptionsStyle.down, icons.iconChevronDownMedium)}/>
              <i className={classNames(filterOptionsStyle.up, icons.iconChevronUpMedium)}/>*/}
            </section>
            <section className={filterOptionsStyle.filterDrawer}>
              <FilterDrawer drawerOpen={drawerOpen}
                            filterFields={(this.generateFilterFields(paymentStates, years,
                              this.state, false): any)}
                            color="lightBlue"
                            handleCloseDrawer={this.handleCloseDrawer}
                            handleOpenDrawer={this.handleOpenDrawer}
                            handleClearFilters={this.handleClearFilters}/>
            </section>
          </section>
        </section>
        <section key="mainWrap" className={classNames(commonStyle.mainWrap, commonStyle.nonPaddingTop)}>
          <aside className={commonStyle.filterWrap}>
            {filterContent}
          </aside>
          <section className={commonStyle.rightWrap}>
            <AlertContainer showId="payments"/>
            {!!tags.length && <ul className={filterStyles.tags}>
              {tags}
            </ul>}
            <header>
              <FormattedMessage id={noResult ? 'payments.paymentBox.noResults' : 'payments.paymentBox.title'}>{
                txt => <p>{txt}</p>
              }</FormattedMessage>
            </header>
            <InfiniteScroll offset={650}
                             disabled={!!isFetchingPaymentsMade || canAsk === false}
                             handleCheckpoint={() => fetchPaymentsMade(this.buildQueryParams())}>
              <section className={classNames(commonStyle.paymentsWrap, style.paymentsWrap)}>
                {payments}
              </section>
            </InfiniteScroll>
            {isFetchingPaymentsMade && <Loading className={commonStyle.loading}/>}
          </section>
        </section>
      </Fragment>);
  }
}

PaymentsMade.propTypes = {
  fetchPaymentsMade: PropTypes.func.isRequired,
  isFetchingPaymentsMade: PropTypes.bool,
  paymentsMade: PropTypes.array,
  canAsk: PropTypes.bool,
  fetchPaymentsMadeError: PropTypes.object,
  resetPaymentsState: PropTypes.func.isRequired,
  resetAlertState: PropTypes.func.isRequired,
};

PaymentsMade.contextTypes = {
  intl: intlShape,
};



// WEBPACK FOOTER //
// ./src/modules/payments/components/PaymentsMade/PaymentsMade.jsx